import React from 'react';

const PricingSection = () => {
  // Helper function for features with light background
  const renderFeature = (feature, index, iconColorClass = "text-emerald-500") => (
    <li key={index} className="flex items-start">
      <svg className={`h-5 w-5 ${iconColorClass} mr-3 mt-0.5 flex-shrink-0`} xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
      </svg>
      <span className="text-gray-100 font-medium">{feature}</span>
    </li>
  );

  // Helper for professional features (purple card)
  const renderPremiumFeature = (feature, index) => (
     <li key={index} className="flex items-start">
      <svg className="h-5 w-5 text-yellow-400 mr-3 mt-0.5 flex-shrink-0" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
      </svg>
      <span className="text-white font-medium">{feature}</span>
    </li>
  );

  return (
    <section id="pricing" className="bg-gray-50">

      {/* Header */}
      <div className="text-center pt-24 pb-16 px-4">
        <span className="inline-block py-1.5 px-4 rounded-full text-indigo-700 bg-indigo-100 font-semibold text-sm mb-4">
          PRICING
        </span>
        <h2 className="text-4xl md:text-5xl font-bold mb-5 text-gray-900">
          Invest in Your Trading Success
        </h2>
        <p className="text-xl md:text-2xl text-gray-500 max-w-3xl mx-auto">
          Choose the plan that empowers your trading style and goals.
        </p>
      </div>

      {/* Container for the grid */}
      <div className="container max-w-7xl mx-auto px-4 pb-16">
        {/* Grid layout */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 md:gap-10">

          {/* Card 1: Standard License - Now with teal/cyan gradient */}
          <div className="bg-gradient-to-br from-teal-800 to-teal-900 rounded-xl shadow-lg overflow-hidden border border-teal-700 transform transition duration-300 hover:scale-[1.02] hover:shadow-2xl flex flex-col h-full">
            <div className="p-8 flex flex-col flex-grow">
              <h3 className="text-2xl font-bold text-white mb-2">Monthly License</h3>
              <p className="text-teal-200 text-sm mb-6">For individual traders</p>
              <div className="mb-6">
                <span className="text-5xl font-extrabold text-white">$100</span>
                <span className="text-teal-200 ml-2">one-month payment</span>
              </div>
              <ul className="space-y-4 mb-8 flex-grow">
                {[
                  "Single MT5 Account License",
                  "13 Core Chart Pattern Detection",
                  "Enhanced Pattern Consensus Technology™",
                  "Telegram Notifications",
                  "Advanced Risk Management Suite",
                  "Multi-Timeframe Analysis Feature",
                  "Built-in Black Swan Protection",
                  "Priority Technical Support (1 Year)",
                  "Monthly Standard Technical Support",
                  "Regular Software Updates"
                ].map((feature, index) => renderFeature(feature, index, "text-teal-400"))}
              </ul>
              <button onClick={() => window.location.href='/contact'} className="block w-full text-center py-3 px-6 bg-teal-200 text-teal-900 font-bold rounded-lg hover:bg-teal-100 transition duration-200 mt-auto">Get Started</button>
            </div>
          </div>

          {/* Card 2: Professional License (Highlighted) - Kept the same */}
          <div className="relative bg-gradient-to-br from-indigo-600 to-indigo-800 rounded-xl shadow-xl overflow-hidden border border-indigo-700 transform transition duration-300 hover:scale-[1.02] hover:shadow-2xl flex flex-col h-full">
            <div className="absolute top-4 right-4 bg-yellow-400 text-indigo-900 text-xs font-bold px-3 py-1 rounded-full shadow-md z-10">MOST POPULAR</div>
            <div className="p-8 flex flex-col flex-grow">
              <h3 className="text-2xl font-bold text-white mb-2">Professional License</h3>
              <p className="text-indigo-200 text-sm mb-6">For serious & multi-account traders</p>
              <div className="mb-6">
                <span className="text-5xl font-extrabold text-white">$997</span>
                <span className="text-indigo-200 ml-2">one-time payment</span>
              </div>
              <ul className="space-y-4 mb-8 flex-grow">
                {[
                  "Multiple MT5 Licenses (up to 3)",
                  "13 Core Chart Pattern Detection",
                  "Enhanced Pattern Consensus Technology™",
                  "Advanced Risk Management Suite",
                  "Multi-Timeframe Analysis Feature",
                  "Built-in Black Swan Protection",
                  "Performance Analytics Dashboard",
                  "Priority Technical Support (1 Year)",
                  "Lifetime Software Updates"
                ].map((feature, index) => renderPremiumFeature(feature, index))}
              </ul>
              <button onClick={() => window.location.href='/contact'} className="block w-full text-center py-3 px-6 bg-white text-indigo-600 font-bold rounded-lg hover:bg-gray-100 transition duration-200 mt-auto">Get Started</button>
             </div>
          </div>

          {/* Card 3: Institutional License - Now with dark blue gradient */}
          <div className="bg-gradient-to-br from-blue-900 to-navy-900 rounded-xl shadow-lg overflow-hidden border border-blue-800 transform transition duration-300 hover:scale-[1.02] hover:shadow-2xl flex flex-col h-full">
            <div className="p-8 flex flex-col flex-grow">
              <h3 className="text-2xl font-bold text-white mb-2">Institutional License</h3>
              <p className="text-blue-200 text-sm mb-6">For funds, prop firms & institutions</p>
              <div className="mb-6">
                <span className="text-5xl font-extrabold text-white">Custom</span>
                <span className="text-blue-200 block mt-1">Tailored enterprise solutions</span>
              </div>
              <ul className="space-y-4 mb-8 flex-grow">
                {[
                  "Unlimited MT5 Account Licenses",
                  "All Professional Tier Features",
                  "Custom Pattern Development Options",
                  "Full API Integration Capabilities",
                  "Dedicated Account Manager",
                  "Bespoke Risk Management Configuration",
                  "White Label & Branding Options",
                  "24/7 Premium Enterprise Support",
                  "Advanced & Custom Reporting"
                ].map((feature, index) => renderFeature(feature, index, "text-blue-400"))}
              </ul>
              <button onClick={() => window.location.href='/contact'} className="block w-full text-center py-3 px-6 bg-blue-200 text-blue-900 font-bold rounded-lg hover:bg-blue-100 transition duration-200 mt-auto">Contact Sales</button>
            </div>
          </div>

          {/* Card 4: Partnership Program - Now with purple gradient */}
          <div className="bg-gradient-to-br from-purple-800 to-purple-900 rounded-xl shadow-lg overflow-hidden border border-purple-700 transform transition duration-300 hover:scale-[1.02] hover:shadow-2xl flex flex-col h-full">
            <div className="p-8 flex flex-col flex-grow">
              <h3 className="text-2xl font-bold text-white mb-2">Partnership Program</h3>
              <p className="text-purple-200 text-sm mb-6">Collaborate & grow with us</p>
              <div className="mb-6">
                <span className="text-5xl font-extrabold text-white">Let's Talk</span>
                <span className="text-purple-200 block mt-1">Tailored benefits for partners</span>
              </div>
              <ul className="space-y-4 mb-8 flex-grow">
                {[
                  "Generous Commission Structures",
                  "Marketing Materials & Resources",
                  "Dedicated Partner Manager",
                  "Co-branding Opportunities",
                  "Affiliate Tracking Dashboard",
                  "Early Access to New Features",
                  "Priority Partner Support",
                  "Ideal for IBs, Affiliates, Educators",
                  "Deposit $10,000 with our IB link and get a free license"
                ].map((feature, index) => renderFeature(feature, index, "text-purple-400"))}
              </ul>
              <button onClick={() => window.location.href='/contact'} className="block w-full text-center py-3 px-6 bg-purple-200 text-purple-900 font-bold rounded-lg hover:bg-purple-100 transition duration-200 mt-auto">Become a Partner</button>
             </div>
          </div>

        </div> {/* End Grid */}
      </div> {/* End Container */}

      {/* Footer Guarantee Text */}
      <div className="text-center max-w-3xl mx-auto px-4 pt-0 pb-24">
        <p className="text-lg text-gray-600">
          All plans include our <span className="font-semibold text-gray-700">30-day money-back guarantee</span>. If HON-SONAR™ doesn't meet your expectations, we'll refund your investment, no questions asked.
        </p>
      </div>

    </section>
  );
};

export default PricingSection;